<template>
<CustomField :label="label" :error="error" class="delivery-address-suggestions-component">
  <!-- If it is not filterable, the amount of inputs may suddenly jump when new query is loaded. -->
  <VueSelect :options="vueSelectOptions"
             @input="onSuggestionSelect"
             :getOptionKey="o => `${o.data.fias_id}-flat-${o.data.flat}`"
             :disabled="disabled"
             slot-scope="customFieldSlotProps"
             :inputId="customFieldSlotProps.id"
             :searchable="true"
             :clearable="false"
             :filterable="true"
             :filter="filterLocally"
             label="value"
             searchInputQuerySelector="input"
             @search:blur="$emit('blur', $event)"
             @search:focus="$emit('focus', $event)"
  >
    <template slot="search" slot-scope="props">
      <!-- `@mousedown.stop` `@click.stop` so the dropdown is not toggled when we click on the input when
                the dropdown is open. -->
      <input class="form-control"
             v-bind="getModifiedSearchAttrs(props.attributes)"
             v-on="getModifiedSearchListeners(props.events)"
             @mousedown.stop
             @click.stop
      >
    </template>
    <template slot="no-options">{{ noOptionsText }}</template>
    <!-- Do not render stuff that we don't need.
            Passing nothing would make the slots render their default contents. -->
    <template slot="selected-option-container"><div v-show="false"/></template>
    <template slot="open-indicator"><div v-show="false"/></template>
  </VueSelect>
</CustomField>
</template>

<script>
import axios from '@/zenky'
import VueSelect from 'vue-select';
import CustomField from '@/components/inputs/CustomField';
import throttle from 'lodash/throttle';
import { mapGetters } from 'vuex'

export default {
  components: {
    VueSelect, CustomField
  },

  props: {
    value: {
      required: true,
      type: Object,
    },
    label: {
      required: true,
      type: String,
    },
    params: {
      required: true,
      type: Object,
    },
    error: {
      required: false,
      type: String,
    },
    placeholder: {
      required: false,
      type: String,
      default: '',
    },
    restrictedToCity: {
      required: true,
      type: Boolean,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters({
      activeCity: 'store/activeCity',
    }),

    vueSelectOptions() {
      return this.suggestions.length !== 0 ? this.suggestions : []
    },

    query() {
      return this.value.text;
    },

    noOptionsText() {
      const nothingFoundText = 'Ничего не найдено';

      if (this.value.text.length === 0) {
        return 'Введите название';
      } else if (this.lastStartedPendingRequest !== null) {
        return 'Загрузка...';
      } else if (this.suggestions.length === 0) {
        return nothingFoundText;
      }

      return nothingFoundText;
    },

    throttleRunSuggestionQuery() {
      return throttle(this.runSuggestionQuery, 500);
    },

    // We need local filtering so when the user tries to select an option while the new query is loading, the list
    // would not change when it is finally loaded, resulting in forcing the user to scan through it again.
    filterLocally() {
      return (suggestions, search) => {
        const filtered = suggestions.filter(daData => daData.value.toLowerCase().includes(search.toLowerCase()));

        // In case local filtering filtered some options that are actually viable.
        // This may happen e.g. when the place name is searched in translit.

        if (filtered.length === 0) {
          return suggestions;
        }

        return filtered;
      };
    }
  },

  methods: {
    // Though we pass the input as a slot to `vue-select`, it's mostly controlled by that component.
    // Check out `vue-select` source to see more.
    getModifiedSearchAttrs(vueSelectSearchAttrs) {
      return {
        ...vueSelectSearchAttrs,
        value: this.value.text,
        type: 'text',
        placeholder: this.placeholder,
      };
    },

    getModifiedSearchListeners(vueSelectSearchListeners) {
      return {
        ...vueSelectSearchListeners,
        input: (e) => {
          vueSelectSearchListeners.input(e);
          this.$emit('input', { text: e.target.value, suggestion: null })
        },
      };
    },

    onSuggestionSelect(daData) {
      this.$emit('input', { text: this.query, suggestion: daData.data })
    },

    async runSuggestionQuery() {
      if (!this.query) {
        this.suggestions = []

        return
      }

      const payload = {
        query: this.query,
      }

      if (this.activeCity && this.activeCity.id) {
        payload.city_id = this.activeCity.id
      }

      const currentRequest = axios.post('suggestions/address', payload);
      this.lastStartedPendingRequest = currentRequest;
      let thisStartedTheLast;
      let data;

      try {
        ({ data } = await currentRequest);
      } catch(err) {
        throw err;
      } finally {
        thisStartedTheLast = this.lastStartedPendingRequest === currentRequest;

        // It might have been overriden by a new one.
        if (thisStartedTheLast) {
          this.lastStartedPendingRequest = null;
        }
      }

      if (thisStartedTheLast || this.lastStartedPendingRequest !== null) {
        this.suggestions = data.data.suggestions;
        // Otherwise this request brought us data that is older than the data that we already have.
      }
    },
  },

  watch: {
    query(current, old) {
      if (!!old && !current) {
        this.$emit('cleared')
      }

      if (this.skipNextChange) {
        this.skipNextChange = false

        return
      }

      this.throttleRunSuggestionQuery();
    },
  },

  data() {
    return {
      skipNextChange: false,
      lastStartedPendingRequest: null, // `null` if the last started request has finished.
      suggestions: [],
    }
  }
}
</script>

<style lang="less">
.delivery-address-suggestions-component {
  .v-select {
  @apply relative;
    .vs__dropdown-menu {
    @apply overflow-hidden; // For rounded borders.
    @apply bg-white absolute min-w-full z-10 rounded;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
      top: calc(100% + theme('margin.1'));
      .vs__dropdown-option, .vs__no-options {
      @apply px-3 py-2 font-light text-sm text-grey-darkest whitespace-no-wrap;
      }
      .vs__dropdown-option {
      @apply cursor-pointer;
        &--highlight {
        @apply bg-primary-lightest;
        }
      }
    }
  }
}
</style>
